import React, { useEffect } from 'react';
import { Button, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useOktaRuntime } from '@/OktaProvider';

/**
 * Okta login button component that handles SSO authentication
 */
const OktaLoginButton: React.FC = () => {
  const { oktaAuth, isEnabled, isReady } = useOktaRuntime();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isEnabled || !isReady) return;
    const getToken = async () => {
      const authState = await oktaAuth?.authStateManager?.getAuthState?.();
      if (authState?.isAuthenticated && oktaAuth) {
        try {
          await oktaAuth.getUser();

          message.success('Successfully logged in with Okta!');

          const originalUri = oktaAuth.getOriginalUri?.();
          navigate(originalUri || '/dashboard');
        } catch (error) {
          console.error('Error getting token:', error);
          message.error('Failesd to get authentication token');
        }
      }
    };
    getToken();
  }, [isEnabled, isReady, oktaAuth, navigate]);

  const handleLogin = async (): Promise<void> => {
    try {
      localStorage.removeItem('@token');
      localStorage.removeItem('@refreshToken');
      localStorage.removeItem('persist:root');
      // Store the current path for redirecting back after login
      const originalUri: string = window.location.pathname;

      // Redirect to Okta for authentication
      sessionStorage.setItem('okta_login_initiated', '1');
      if (oktaAuth?.signInWithRedirect) {
        await oktaAuth.signInWithRedirect({
          originalUri: originalUri === '/login' ? '/' : originalUri,
        });
      } else {
        // In tests without provider, no-op but avoid crashing
        console.warn('Okta auth not initialized; skipping redirect');
      }
    } catch (error) {
      console.error('Error during Okta login:', error);
    }
  };

  return isEnabled && isReady ? (
    <>
      <Button
        type='primary'
        onClick={handleLogin}
        data-testid='okta-login-button'
        style={{
          width: '100%',
          height: '36px',
          backgroundColor: '#597EF7',
          borderColor: '#597EF7',
          // @ts-expect-error - TODO fix this hover selector
          // (This was present in the original JavaScript)
          '&:hover': {
            backgroundColor: '#0068a5',
            borderColor: '#0068a5',
          },
          '&:active': {
            backgroundColor: '#005c8f',
            borderColor: '#005c8f',
          },
        }}
        block
      >
        Sign in with Okta
      </Button>
    </>
  ) : null;
};

export default OktaLoginButton;
